import type { PathOptions } from 'leaflet';
import type {
  StandColoringStrategy,
  StandLayerKeyArgs,
  StyleCoreArgs,
} from './FmStandMap.types';
import {
  OPERATION_COLORS,
  UNTOUCHED_COLOR,
  type OperationColorKey,
} from '@/utils/colorSwatches';
import { paletteSpecies } from '@/components/ui/charts/palette';
import { getStandOverlayStyle } from '@/shared/registries';
import { getForestStandPathOptions } from '@/components/ui/FmStandMapSemantics';
import { servedStandSpeciesCode } from '@/types/generated/forest_models';
export { featureStandId } from '@/utils/featureStandId';

export const NEUTRAL_FOREST = 'var(--fm-map-forest-a)';

const TIME_HORIZON_COLORS = {
  near: 'var(--fm-accent-strong)',
  mid: 'var(--fm-accent)',
  far: 'var(--fm-accent-dim)',
} as const;

function operationColorFor(props: Record<string, unknown>): string {
  const raw = String(
    props.operation_type ?? props.operationType ?? props.operation ?? props.type ?? '',
  ).toLowerCase();
  if (!raw) return UNTOUCHED_COLOR;
  if (raw in OPERATION_COLORS) return OPERATION_COLORS[raw as OperationColorKey];
  if (raw.includes('clear') || raw.includes('final')) return OPERATION_COLORS.clearcut;
  if (raw.includes('thin')) return OPERATION_COLORS.thinning;
  if (raw.includes('seed')) return OPERATION_COLORS['seed-tree'];
  if (raw.includes('plant')) return OPERATION_COLORS.planting;
  if (raw.includes('tend')) return OPERATION_COLORS.tending;
  return UNTOUCHED_COLOR;
}

function timeHorizonColorFor(props: Record<string, unknown>): string {
  const bucket = String(props.time_horizon ?? props.timeHorizon ?? '').toLowerCase();
  if (bucket === 'near' || bucket === 'mid' || bucket === 'far') {
    return TIME_HORIZON_COLORS[bucket];
  }
  const year = Number(props.operation_year ?? props.year);
  if (Number.isFinite(year)) {
    const nowYear = new Date().getFullYear();
    const delta = year - nowYear;
    if (delta <= 5) return TIME_HORIZON_COLORS.near;
    if (delta <= 15) return TIME_HORIZON_COLORS.mid;
    return TIME_HORIZON_COLORS.far;
  }
  return UNTOUCHED_COLOR;
}

function densityOpacity(props: Record<string, unknown>): number {
  const volumePerHa = Number(
    props.volume_per_hectare ??
      (props.volume_m3 && props.area_hectares
        ? Number(props.volume_m3) / Number(props.area_hectares)
        : 0),
  );
  if (!Number.isFinite(volumePerHa) || volumePerHa <= 0) return 0.3;
  if (volumePerHa <= 60) return 0.3;
  if (volumePerHa >= 300) return 0.8;
  return 0.3 + ((volumePerHa - 60) / 240) * 0.5;
}

export function strategyBaseColor(
  strategy: StandColoringStrategy,
  props: Record<string, unknown>,
): string {
  switch (strategy) {
    case 'forest':
    case 'species': {
      const code = servedStandSpeciesCode(props);
      return code === null ? NEUTRAL_FOREST : paletteSpecies(code);
    }
    case 'density':
      return NEUTRAL_FOREST;
    case 'operation':
      return operationColorFor(props);
    case 'timeHorizon':
      return timeHorizonColorFor(props);
    case 'selection':
    case 'outline':
    case 'custom':
    default:
      return NEUTRAL_FOREST;
  }
}

function outlineStandStyle({
  selected,
  focused,
  selectable,
}: Pick<StyleCoreArgs, 'selected' | 'focused' | 'selectable'>): PathOptions {
  return {
    color: selectable ? 'var(--fm-d-fg)' : 'var(--fm-d-fg-2)',
    fillColor: 'transparent',
    fillOpacity: 0,
    weight: selected || focused ? 3 : 2,
    opacity: focused ? 1 : selectable ? 0.92 : 0.4,
  };
}

export function coreStandStyle({
  strategy,
  selected,
  focused,
  affected,
  selectable,
  baseColor,
  props,
}: StyleCoreArgs): PathOptions {
  if (strategy === 'outline') {
    return outlineStandStyle({ selected, focused, selectable });
  }

  if (!selectable) {
    return {
      color: 'var(--fm-d-fg-2)',
      fillColor: 'var(--fm-d-fg-2)',
      weight: 1,
      opacity: 0.28,
      fillOpacity: 0.08,
    };
  }

  const baseStyle: PathOptions = strategy === 'forest'
    ? getForestStandPathOptions(props)
    : {
        color: baseColor,
        fillColor: baseColor,
        weight: 2,
        opacity: 0.85,
        fillOpacity: strategy === 'density' ? densityOpacity(props) : 0.28,
      };

  // The unselected weight already folds in the strategy's own emphasis (e.g. a
  // mature forest stand renders at weight 3 while an immature one renders at 2).
  const unselectedWeight = Math.max(
    Number(baseStyle.weight ?? 2),
    focused ? 4 : affected ? 3 : 2,
  );

  // Selection is an interaction layer, not a replacement for the information
  // encoded by the current strategy. Preserve the semantic fill (species,
  // operation, density, or maturity) and add emphasis through the shared halo.
  // The selected outline must always read as STRICTLY heavier than the same
  // stand's unselected outline — clamping both to a shared floor erased the
  // emphasis for stands whose semantic base weight already met that floor
  // (e.g. mature stands), so add on top of the unselected weight instead.
  if (selected) {
    const selection = getStandOverlayStyle('selected');
    return {
      ...baseStyle,
      ...selection,
      className: [baseStyle.className, selection.className]
        .filter(Boolean)
        .join(' '),
      weight: unselectedWeight + (focused ? 2 : 1),
    };
  }

  return {
    ...baseStyle,
    weight: unselectedWeight,
    opacity: focused ? 1 : baseStyle.opacity,
  };
}

export function buildStandLayerKey({
  coloringStrategy,
  focusedStandId,
  selectedStandIds,
  selectableStandIds,
  affectedStandIds,
  standLayerKey,
}: StandLayerKeyArgs): string {
  const sel = selectedStandIds ? [...selectedStandIds].sort().join(',') : '';
  const selectable = selectableStandIds
    ? [...selectableStandIds].sort().join(',')
    : 'all';
  const affected = affectedStandIds ? [...affectedStandIds].sort().join(',') : '';
  const dataKey = standLayerKey ? `-${standLayerKey}` : '';
  return `standmap-${coloringStrategy}-${focusedStandId ?? ''}-${sel}-${selectable}-${affected}${dataKey}`;
}
